import "./style.scss";

import {
  CSATData,
  CustomerSatisfactionSheet,
  FeedbackMediaType,
} from "@amzn/stencil-react-components/customer-satisfaction";
import { Container } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContainer } from "@amzn/stencil-react-components/modal";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useCallback, useMemo, useState } from "react";

export interface CustomerFeedbackModalProps {
  touchpoint: string;
  analyticsEventPrefix: string;
  close: () => void;
  mediaProps?: {
    media: {
      altText: string;
      src: string;
      type: FeedbackMediaType;
    };
  };
  onSubmitSuccess?: (ratingIndex: number) => void;
}

export const CustomerFeedbackModal: FC<CustomerFeedbackModalProps> = ({
  touchpoint,
  analyticsEventPrefix,
  close,
  mediaProps,
  onSubmitSuccess,
}) => {
  const [hasErrors, setHasErrors] = useState(false);
  const { addMessageBanner } = useMessageBannersBox();
  const storageKey = useMemo(() => `feedback-score-${touchpoint}`, [touchpoint]);

  const [score, setScore] = useState<number | undefined>(() => {
    const saved = sessionStorage.getItem(storageKey);
    const parsed = saved ? Number(saved) : undefined;
    return Number.isFinite(parsed) ? parsed : undefined;
  });

  const handleScoreChange = useCallback(
    (newScore: number) => {
      setHasErrors(false);
      setScore(newScore);
      sessionStorage.setItem(storageKey, String(newScore));
    },
    [storageKey]
  );

  const handleCancel = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    close();
  }, [storageKey, close]);

  const handleSubmit = useCallback(
    (data: CSATData) => {
      setHasErrors(false);
      const finalScore = Number.isFinite(data.selectedRating)
        ? data.selectedRating! + 1
        : score !== undefined
        ? score + 1
        : undefined;
      if (finalScore === undefined) {
        setHasErrors(true);
        return;
      }

      const payload: Record<string, any> = { score: finalScore };
      const comment = typeof data.optionalData === "string" ? data.optionalData.trim() : "";
      if (comment.length > 0) {
        payload.comment = comment;
      }

      trackUxmCustomEvent(`${analyticsEventPrefix}-${touchpoint}`, payload);
      addMessageBanner(getString("customerFeedback.sheet.successMessage"), MessageBannerType.Success);

      sessionStorage.removeItem(storageKey);
      onSubmitSuccess?.(finalScore - 1);
      close();
    },
    [touchpoint, analyticsEventPrefix, storageKey, addMessageBanner, close, score, onSubmitSuccess]
  );

  const imageProps = mediaProps ? { desktopMediaProps: mediaProps, mobileMediaProps: mediaProps } : {};

  return (
    <ModalContainer maxWidth="S1200">
      <Container paddingHorizontal="S500">
        <CustomerSatisfactionSheet
          {...imageProps}
          className={mediaProps ? undefined : "cfm-no-media"}
          ratingProps={{
            onChange: handleScoreChange,
            options: getString("customerFeedback.sheet.ratingOptions") as unknown as string[],
            titleText: getString("customerFeedback.sheet.ratingTitle"),
            value: score,
          }}
          feedbackCardProps={{
            hasErrors,
            hasOptionalTextArea: true,
            optionalContentLabel: getString("customerFeedback.sheet.feedbackLabel"),
          }}
          onCancelClick={handleCancel}
          shouldShowLaterButton={false}
          titleText={getString("customerFeedback.sheet.title")}
          onSubmit={handleSubmit}
        />
      </Container>
    </ModalContainer>
  );
};
